import moment from "moment";
import { getJobLabel, getStatusLabel } from "./workerHelper";
import { formatDate, salaryFormatter } from "./formatters";
import { FormattedMessage } from "react-intl";
import _ from "lodash";
import {
  POPUP_STATUS,
  QUERY_STATUS,
  LANG,
  STATUS_MISSION,
  MANAGER_TOOLS_SECTIONS,
} from "constants/constants";
import { getAccessibleSections } from "access-roles/general-functions";
import Loader from "core/loader/LoaderV2";
import NoNesultIcon from "assets/icons/no-result.svg";
import {
  ONE_FIELD_IS_MANDATORY,
  THREE_CHARACTERS_MINI_MISSION_NAME,
} from "constants/popup-codes";

const formatDurationParts = ({ years, months, lang }) => {
  const parts = [];
  if (lang === LANG.FR) {
    if (years > 0) parts.push(`${years} an${years > 1 ? "s" : ""}`);
    parts.push(`${months} mois`);
  } else {
    if (years > 0) parts.push(`${years} year${years !== 1 ? "s" : ""}`);
    parts.push(`${months} month${months !== 1 ? "s" : ""}`);
  }
  return parts.join(", ");
};

// Tab opened when the URL names none: the first one the user may see, in the
// order NavigationBar renders them.
export const getDefaultToolsSection = ({ sections, userRoleList }) =>
  getAccessibleSections({ sections, userRoles: userRoleList })[0]?.section ??
  MANAGER_TOOLS_SECTIONS.missions;

export const calcDuration = ({ start, end, status, lang }) => {
  if (status === STATUS_MISSION.OK) {
    return "-";
  }

  const startDate = moment(start);
  const endDate = _.isNil(end) ? moment() : moment(end);
  const duration = moment.duration(endDate.diff(startDate));
  let years = duration.years();
  let months = duration.months();

  if (duration.days() > 0) {
    months += 1;
  }
  if (months >= 12) {
    years += 1;
    months = 0;
  }

  return formatDurationParts({ years, months, lang });
};

export const getWorkerSlug = (id, list) =>
  list?.find((elm) => elm?.id === id)?.slug;

export const getClientSlug = (id, list) =>
  list?.find((elm) => elm?.id === id)?.slug;

export const sortTriggerHelper = ({ header, sortCondition }) => {
  return header === sortCondition ? "arrow-up" : "arrow-down";
};

export const formatMissionData = ({
  data,
  getConsultantName,
  lang,
  jobList,
  workersList,
  clientsList,
}) => {
  const mission_title = data?.name;
  const client = clientsList?.find((e) => e.id === data.client_id)?.full_name;
  const consultant = getConsultantName({
    workers: workersList,
    id: data?.worker_id,
  });
  const manager = getConsultantName({
    workers: workersList,
    id: data?.manager_id,
  });

  const job = getJobLabel(jobList, data?.domain_id, lang);
  const status = getStatusLabel(data?.status);
  const mission_start_date =
    lang === LANG.FR ? formatDate(data?.start_date) : data?.start_date;
  const TJ = data?.current_fee ? salaryFormatter(data.current_fee, false) : "-";
  const mission_id = data?.id;
  const desc = data?.description;
  const duration = calcDuration({
    start: data?.start_date,
    end: data?.end_date,
    status: data?.status,
    lang,
  });

  return {
    ...data,
    mission_id,
    client,
    consultant,
    manager,
    job,
    status,
    mission_start_date,
    mission_title,
    duration,
    TJ,
    desc,
  };
};

export const renderInitialLoaderOrNoResult = ({
  status,
  hasContent,
  page = 1,
}) => (
  <>
    {status === QUERY_STATUS.LOADING && !hasContent && (
      <Loader key={page} id={page} />
    )}
    {!hasContent && status === QUERY_STATUS.SUCCESS && (
      <div className="no-result-wrapper">
        <img
          src={NoNesultIcon}
          alt="no-result-icon"
          className="no-result-icon"
        />
        <p className="title-4 violet no-result-text">
          <FormattedMessage
            id="no_results"
            defaultMessage="Oops, aucun résultat pour cette recherche."
          />
        </p>
      </div>
    )}
  </>
);

const newSortValue = (sort) => (sort === "asc" ? "desc" : "asc");

export const sortHandler = ({
  sortKey,
  currentSort,
  setSort,
  getter,
  filters,
}) => {
  const newSort = newSortValue(currentSort);
  setSort((prev) => ({
    ...prev,
    [sortKey]: newSort,
  }));
  getter({ filters, isToolsManager: true, orderBy: `${sortKey}-${newSort}` });
};

export const isValidFilters = ({ filters, setStatus, setCode }) => {
  const { sector_id, domain_category_id, domain_id } = filters || {};
  const query = filters?.query?.trim();
  // "Tous" (id 0) is dropped by jsonToQueryParams, so it is no sector filter
  const sectors = [].concat(sector_id ?? []).filter((id) => id !== "");
  const hasSector = sectors.length > 0 && !sectors.includes(0);
  if (!query && !hasSector && !domain_category_id && !domain_id) {
    setStatus(POPUP_STATUS.ERROR);
    setCode(ONE_FIELD_IS_MANDATORY);
    return false;
  } else if (query && query?.length < 3) {
    setStatus(POPUP_STATUS.ERROR);
    setCode(THREE_CHARACTERS_MINI_MISSION_NAME);
    return false;
  }
  return true;
};

export const jsonToQueryParams = (obj) => {
  if (_.isEmpty(obj)) return null;
  return `&${_.chain(obj)
    .map((value, key) => {
      if (key === "sector_id" && value?.some((e) => e === 0)) return null;
      if (key === "impact" && value === false) return null;
      if (value === 0) return null;
      return `${key}=${
        typeof value === "string" ? encodeURIComponent(value) : value
      }`;
    })
    .compact()
    .join("&")
    .value()}`;
};

export const cleanFiltersHelper = (filters) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([filter, value]) =>
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
    )
  );
